import { useEffect, useMemo, useReducer, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import {
  doCreateData,
  doDeleteData,
  doGetAuditsData,
  doGetData,
  doSearch,
  doUpdateData,
  emptyOrganization,
  luHandleAppiError,
  normalizeU2104OrganizationFromApi,
  type U2104Organization,
} from "../U2104Service";
import { useU2104Context } from "../U2104Context";
import { NS, U2104ROLES } from "../U2104";
import U2104OrganizationDeleteDlg from "./common/U2104OrganizationDeleteDlg";
import { U2104OrganizationFormToolBar } from "./form/u2104Organization/U2104OrganizationFormToolBar";
import U2104OrganizationBody from "./form/u2104Organization/U2104OrganizationBody";
import U2104OrganizationRoleBody from "./form/u2104Organization/u2104OrganizationRoleTab/U2104OrganizationRoleBody";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import U2104OrganizationUserBody from "./form/u2104Organization/u2104OrganizationAffiliationTab/U2104OrganizationAffiliationBody";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U2104OrganizationPersonalizationBody from "./form/u2104Organization/u2104OrganizationPersonalizationTab/U2104OrganizationPersonalizationBody";
import U2104OrganizationAvailableLogicalUnitBody from "./form/u2104Organization/u2104OrganizationAvailableLogicalUnitTab/U2104OrganizationAvailableLogicalUnitBody";
import U2104OrganizationGoogleCalendarBody from "./form/u2104Organization/u2104OrganizationGoogleCalendarTab/U2104OrganizationGoogleCalendarBody";
import { flushPendingOrganizationIconChanges } from "./form/u2104Organization/U2104UseOrganizationIconHandlers";
import {
  clearOrganizationIconPendingAttributes,
  getOrganizationIconPending,
  hasOrganizationIconPendingChanges,
} from "./form/u2104Organization/u2104OrganizationIconPending";

// Interface à placer en dehors du composant pour éviter l'erreur de modificateur
export interface U2104OrganizationFormExtendedHandlers extends U2000FormStateHandlersReturn<U2104Organization> {
  addNewOrganization: () => void;
  modifyOrganization: () => void;
  deleteOrganization: () => Promise<void>;
}

const U2104OrganizationForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  // Form state and reducer for u2104Organization
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  // navigate et location
  const location = useLocation();
  const navigate = useNavigate();

  // Translation
  const { t, i18n } = useTranslation();
  const toast = useToast();

  // API Axios Client
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  // LU Handler,  Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridStateHandlers, orgAffiliationCurrentTab, setOrgAffiliationCurrentTab } = useU2104Context();
  const [googleCalendarStatusRefreshKey, setGoogleCalendarStatusRefreshKey] = useState(0);

  // Form Handlers and default methods
  const validateForm = (organization: U2104Organization) => {
    const result: Record<string, string> = {};
    if (!organization.organizationName || organization.organizationName.trim() === "") {
      result["username"] = t("errUserNameRequired", { ns: NS });
    }
    return result;
  };

  // Business Logic Handlers for U2104Organization
  const u2104OrganizationBL: U2000FormSBusinessHandlers<U2104Organization> = {
    newRecord: () => {
      return emptyOrganization;
    },

    // validation du formulaire
    validateForm: validateForm,

    // sauvegarde du formulaire
    saveForme: async (organization: U2104Organization, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(organization);
      if (Object.keys(errors).length === 0) {
        await handleSave.saveData(organization, editMode);
      } else {
        u2104OrganizationFormStateExtendeHandlers.setErrors(errors);
      }
    },

    // annulation du formulaire
    cancelForm: () => {
      const organization = u2104OrganizationFormStateExtendeHandlers.getOldData() as U2104Organization | null;
      if (organization) {
        // Met à false checkedId pour chaque action
        if ("roles" in organization && Array.isArray((organization as any).roles)) {
          (organization as any).roles = (organization as any).roles.map((role: any) => ({ ...role, idChecked: false }));
        }
        u2104OrganizationFormStateExtendeHandlers.setData({ ...organization });
      }
      u2104OrganizationFormStateExtendeHandlers.setCustomAttributes(
        clearOrganizationIconPendingAttributes(u2104OrganizationFormStateExtendeHandlers.getCustomAttributes())
      );

      if (luStateHandlers.getActionOriginFrom() === "/U2104") {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    // gestion du changement d'enregistrement
    onChangeRecordForm: (organization) => {
      if (organization.id) {
        handleGet.getData(organization.id);
      }
    },

    // gestion du changement de page
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    // gestion de la sortie du formulaire
    onLeaveForm: () => {
      navigate("/U2104");
    },
  };

  // Handlers pour le formulaire d'une organisation
  const u2104OrganizationFormStateHandlers = useU2000FormStateHandlers<U2104Organization>({
    formState,
    formStateDispatch,
    businessHandlers: u2104OrganizationBL,
  });

  // extended methods
  const u2104OrganizationFormStateExtendeHandlers: U2104OrganizationFormExtendedHandlers = {
    ...u2104OrganizationFormStateHandlers,

    addNewOrganization: () => {
      u2104OrganizationFormStateExtendeHandlers.setCustomAttributes(
        clearOrganizationIconPendingAttributes(u2104OrganizationFormStateExtendeHandlers.getCustomAttributes())
      );
      u2104OrganizationFormStateExtendeHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    },
    modifyOrganization: () => {
      u2104OrganizationFormStateExtendeHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteOrganization: async () => {
      handleDelete.handleDelete();
    },
  };

  // Call Search Handlers for Logical Unit
  const handleSearch = useU2000SearchHandlers<U2104Organization>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U2104Organization>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Get Handlers for Logical Unit
  const handleGet = useU2000GetHandlers<U2104Organization & { id: string }>({
    doGetFn: (id: string) => {
      return doGetData(u2000AxiosAuthenticated, id);
    },
    onSuccess: (logicalUnit: U2104Organization & { id: string }) => {
      const normalized = normalizeU2104OrganizationFromApi(logicalUnit, i18n.language);
      gridStateHandlers.setDataById(normalized.id, normalized);
      u2104OrganizationFormStateExtendeHandlers.setData(normalized);
      u2104OrganizationFormStateExtendeHandlers.setCustomAttributes(
        clearOrganizationIconPendingAttributes(u2104OrganizationFormStateExtendeHandlers.getCustomAttributes())
      );

      // Remplace l'id dans l'URL par le nouvel id (évite navigate no-op qui re-render)
      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(logicalUnit.id));
      if (newPath !== location.pathname) {
        navigate(newPath, { replace: true });
      }
      setLogicalUnitBreadcrumb([{ href: "/U2104", label: t("title", { ns: NS }) }, { label: logicalUnit.organizationName }]);

      // Détermine le mode du formulaire selon l'action précédente
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u2104OrganizationFormStateExtendeHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }

      // Set breadcrumb for this organization
      try {
        const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
        setLogicalUnitBreadcrumb([{ href: "/U2104", label: t("title", { ns: NS }) }, { label: logicalUnit.organizationName || "" }]);
      } catch (e) {
        // noop
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Save Handlers for Logical Unit
  const handleSave = useU2000SaveHandlers<U2104Organization>({
    doSaveFn: (organization: U2104Organization, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, organization);
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, organization.id, organization);
      }
      // Ensure a Promise is always returned to satisfy the handler's expected signature
      return Promise.resolve(undefined);
    },
    onSuccess: async (organization: U2104Organization & { id: string }, editMode: U2000FormStateEnumEditMode) => {
      let normalized = normalizeU2104OrganizationFromApi(organization, i18n.language);
      const pendingIcon = getOrganizationIconPending(u2104OrganizationFormStateExtendeHandlers.getCustomAttributes());

      if (hasOrganizationIconPendingChanges(pendingIcon) && normalized.id) {
        try {
          const iconUpdated = await flushPendingOrganizationIconChanges(u2000AxiosAuthenticated, normalized.id, pendingIcon);
          if (iconUpdated) {
            normalized = normalizeU2104OrganizationFromApi({ ...normalized, ...iconUpdated }, i18n.language);
          }
        } catch (iconError: unknown) {
          const errorResult = U2000HandleApiError(iconError as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
          if (errorResult.genericErrors?.length) {
            errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
          } else {
            toast.error(t("msgOrganizationIconSaveFailed", { ns: NS }));
          }
        }
      }

      u2104OrganizationFormStateExtendeHandlers.setCustomAttributes(
        clearOrganizationIconPendingAttributes(u2104OrganizationFormStateExtendeHandlers.getCustomAttributes())
      );

      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), normalized]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);

        // Remplace l'id dans l'URL par le nouvel id
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(normalized.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(normalized.id, normalized);
      }
      u2104OrganizationFormStateExtendeHandlers.afterSaveData(normalized);
      toast.success(t("msgOrganizationSaved", { ns: NS }));

      if (orgAffiliationCurrentTab === "tabGoogleCalendar") {
        setGoogleCalendarStatusRefreshKey((k) => k + 1);
      }

      // Retourne à la liste après l'ajout ou la modification selon l'origine
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U2104") {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);

      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u2104OrganizationFormStateExtendeHandlers.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u2104OrganizationFormStateExtendeHandlers.setErrors(errorResult.fieldErrors);
      }
    },
  });

  // Call Delete Handlers for Logical Unit
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u2104OrganizationFormStateExtendeHandlers.getData()],
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, row);
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      let currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row["organizationName"], ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      // Apres suppression, aller au prochain record, aller vers l'avant si possible, sinon aller vers l'arrière, si aucun, retourner à la liste
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.PREVIOUSPAGE);
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.PREVIOUSPAGE);
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate("/U2104", { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // Initialisation au montage et quand id change (ex. F5 sur /U2104/:id : id peut arriver après le 1er rendu).
  useEffect(() => {
    if (id === undefined) return;
    if (id === "new") {
      u2104OrganizationFormStateExtendeHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  // Retour OAuth Google Calendar (?googleCalendar=connected)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("googleCalendar") !== "connected") return;
    toast.success(t("msgGoogleCalendarConnected", { ns: NS }));
    setOrgAffiliationCurrentTab("tabGoogleCalendar");
    setGoogleCalendarStatusRefreshKey((k) => k + 1);
    window.history.replaceState({}, "", location.pathname + location.hash);
  }, [location.search, location.pathname, location.hash, t, toast, setOrgAffiliationCurrentTab]);

  // Tabs du formulaire
  const formTabsComponent: any[] = [
    {
      id: "tabUser",
      label: t("tabUsers", { ns: NS }),
      content: <U2104OrganizationUserBody u2104OrganizationFormHandlers={u2104OrganizationFormStateExtendeHandlers} />,
    },
    {
      id: "tabRole",
      label: t("tabRoles", { ns: NS }),
      content: <U2104OrganizationRoleBody u2104OrganizationFormHandlers={u2104OrganizationFormStateExtendeHandlers} />,
    },
    {
      id: "tabPersonalization",
      label: t("tabPersonalization", { ns: NS }),
      content: <U2104OrganizationPersonalizationBody u2104OrganizationFormHandlers={u2104OrganizationFormStateExtendeHandlers} />,
    },
    {
      id: "tabAvailableLogicalUnits",
      label: t("tabAvailableLogicalUnits", { ns: NS }),
      content: <U2104OrganizationAvailableLogicalUnitBody u2104OrganizationFormHandlers={u2104OrganizationFormStateExtendeHandlers} />,
    },
    {
      id: "tabGoogleCalendar",
      label: t("tabGoogleCalendar", { ns: NS }),
      content: (
        <U2104OrganizationGoogleCalendarBody
          u2104OrganizationFormHandlers={u2104OrganizationFormStateExtendeHandlers}
          statusRefreshKey={googleCalendarStatusRefreshKey}
        />
      ),
    },
    hasRole(U2104ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: <U2000AuditPanelComponent id={u2104OrganizationFormStateExtendeHandlers.getData()?.id} doGetAuditsData={doGetAuditsData} toastName="U2104OrganizationAudit" />,
    },
  ].filter(Boolean);

  // Détermination dynamique du defaultTab selon la priorité : Users > Roles > Personalization > UL > Google Calendar > Audits
  const tabPriority = ["tabUser", "tabRole", "tabPersonalization", "tabAvailableLogicalUnits", "tabGoogleCalendar", "tabAudits"];
  const availableTabIds = formTabsComponent.map((tab) => tab.id);
  const defaultTab = orgAffiliationCurrentTab ? orgAffiliationCurrentTab : tabPriority.find((tabId) => availableTabIds.includes(tabId)) || availableTabIds[0];

  // import pour eciter les boocles infinies
  const formEditMode = useMemo(() => u2104OrganizationFormStateExtendeHandlers.getFormEditMode(), [u2104OrganizationFormStateExtendeHandlers]);

  // permet de mettre à jour les donnees apres le changement du mode edit (ADD ou EDIT)
  useEffect(() => {
    if (formEditMode === U2000FormStateEnumEditMode.EDIT) {
    }
  }, [formEditMode]);

  // s'assurer que les données sont chargées avant le rendu
  if (u2104OrganizationFormStateExtendeHandlers.getData() === null) {
    return null;
  }

  // Render
  return (
    <>
      <U2104OrganizationDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />

      <U2000DefaultForm
        actionsBtns={<U2104OrganizationFormToolBar formStateHandlers={u2104OrganizationFormStateExtendeHandlers as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u2104OrganizationFormStateExtendeHandlers as any}
      >
        <U2104OrganizationBody formStateHandlers={u2104OrganizationFormStateExtendeHandlers as any} t={t} />

        <U2000Tab
          className="mt-4"
          tabs={formTabsComponent}
          defaultTab={defaultTab}
          activeTab={orgAffiliationCurrentTab ?? defaultTab}
          setActiveTab={(tab) => setOrgAffiliationCurrentTab(typeof tab === "string" ? tab : String(tab))}
        />
      </U2000DefaultForm>
    </>
  );
};

export default U2104OrganizationForm;
